import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import SidebarLayout from '../../../../layouts/SidebarLayout';
import PageSubHeader from '../../../../components/Layouts/PageSubHeader';
import sidebar from '../../utils/sidebar';
import DefaultButton from '../../../../components/Buttons/Default';
import { TaskCard, TaskCardContent } from '../../components/Cards/Task';
import { get, post } from '../../../../utils/helpers/api';
import './styles.css';
import { TfiFilter } from 'react-icons/tfi';
import { Badge } from 'reactstrap';
import FiltersDrawer from './Drawers/Filters';
import { useTranslation } from 'react-i18next';

const COLUMNS = [
  { id: 'not_started', color: '#6e6e73' },
  { id: 'deferred',    color: '#f59e0b' },
  { id: 'in_progress', color: '#3b82f6' },
  { id: 'completed',   color: '#10b981' },
  { id: 'cancelled',   color: '#ef4444' },
];

const COLUMN_LABEL_KEYS = {
  not_started: 'common.notStarted',
  deferred: 'common.deferred',
  in_progress: 'common.inProgress',
  completed: 'common.completed',
  cancelled: 'common.canceled',
};

const COLUMN_IDS = COLUMNS.map(c => c.id);
const PRIORITY_LABEL_KEYS = { high: 'common.high', normal: 'common.normal', low: 'common.low' };

const toPlaceholder  = (colId) => `__ph__${colId}`;
const isPlaceholder  = (id)    => id.startsWith('__ph__');
const placeholderCol = (id)    => id.slice(6);

function findContainer(id, columnOrder) {
  if (COLUMN_IDS.includes(id)) return id;
  if (isPlaceholder(id)) return placeholderCol(id);
  return Object.keys(columnOrder).find(colId => columnOrder[colId].includes(id));
}

function collisionDetection(args) {
  const hits = pointerWithin(args);
  if (hits.length > 0) {
    const card = hits.find(c => !COLUMN_IDS.includes(c.id));
    return card ? [card] : [hits[0]];
  }
  return closestCenter(args);
}

function ColumnPlaceholder({ id }) {
  const { setNodeRef } = useSortable({ id });
  return <div ref={setNodeRef} style={{ flexGrow: 1, minHeight: 60 }} />;
}

function KanbanColumn({ column, taskIds, tasks }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const isEmpty = taskIds.length === 0;
  const placeholderId = toPlaceholder(column.id);

  return (
    <div className={`kanban-column${isOver ? ' is-over' : ''}`}>
      <div className="kanban-column-header">
        <div className="kanban-column-dot" style={{ backgroundColor: column.color }} />
        <span className="kanban-column-title">{column.label}</span>
        <span className="kanban-column-count">{taskIds.length}</span>
      </div>
      <div ref={setNodeRef} className="kanban-column-body">
        <SortableContext
          items={isEmpty ? [placeholderId] : taskIds}
          strategy={verticalListSortingStrategy}
        >
          {taskIds.map(id => tasks[id] && (
            <TaskCard key={id} task={tasks[id]} />
          ))}
          {isEmpty && <ColumnPlaceholder id={placeholderId} />}
        </SortableContext>
      </div>
    </div>
  );
}

function TasksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks]             = useState({});
  const [columnOrder, setColumnOrder] = useState(Object.fromEntries(COLUMNS.map(c => [c.id, []])));
  const [activeId, setActiveId]       = useState(null);
  const [loading, setLoading]         = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const dragOrigin = useRef(null);

  const FILTER_KEYS = ['subject', 'priority', 'assigned_to', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(v => v).length;

  const normalizeTask = (task) => ({
    ...task,
    id: String(task.id),
    priority: PRIORITY_LABEL_KEYS[task.priority] ? t(PRIORITY_LABEL_KEYS[task.priority]) : task.priority,
    customer: [task.customer?.firstname, task.customer?.lastname].filter(Boolean).join(' ') || '—',
  });

  const columns = COLUMNS.map(col => ({ ...col, label: t(COLUMN_LABEL_KEYS[col.id]) }));

  const fetchTasks = async () => {
    setLoading(true);
    let query = '/crm/tasks?';
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    Object.entries(activeFilters).forEach(([key, value]) => {
      query += `&${key}=${encodeURIComponent(value)}`;
    });
    const data = await get(query);
    if (data?.success && data?.tasks) {
      const newTasks = {};
      const newOrder = {};
      Object.entries(data.tasks).forEach(([colId, colData]) => {
        const normalized = colData.tasks.map(normalizeTask);
        newOrder[colId]  = normalized.map(t => t.id);
        normalized.forEach(t => { newTasks[t.id] = t; });
      });
      setTasks(newTasks);
      setColumnOrder(prev => ({ ...prev, ...newOrder }));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTasks();
  }, [filters]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setFiltersOpen(false);
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragStart({ active }) {
    setActiveId(active.id);
    dragOrigin.current = findContainer(active.id, columnOrder);
  }

  function handleDragOver({ active, over }) {
    if (!over) return;
    const activeContainer = findContainer(active.id, columnOrder);
    const overContainer   = findContainer(over.id,   columnOrder);
    if (!activeContainer || !overContainer || activeContainer === overContainer) return;
    setColumnOrder(prev => {
      const activeItems = prev[activeContainer];
      const overItems   = prev[overContainer];
      const overIndex   = overItems.indexOf(over.id);
      const insertAt    = overIndex >= 0 ? overIndex : overItems.length;
      return {
        ...prev,
        [activeContainer]: activeItems.filter(id => id !== active.id),
        [overContainer]:   [...overItems.slice(0, insertAt), active.id, ...overItems.slice(insertAt)],
      };
    });
  }

  function handleDragEnd({ active, over }) {
    setActiveId(null);
    if (!over) return;
    const fromContainer = dragOrigin.current;
    const toContainer   = findContainer(active.id, columnOrder);
    dragOrigin.current  = null;
    if (!fromContainer || !toContainer) return;
    if (fromContainer !== toContainer) {
      updateStatus(active.id, fromContainer, toContainer);
      return;
    }
    if (!isPlaceholder(over.id)) {
      const activeIndex = columnOrder[toContainer].indexOf(active.id);
      const overIndex   = columnOrder[toContainer].indexOf(over.id);
      if (activeIndex !== overIndex) {
        setColumnOrder(prev => ({
          ...prev,
          [toContainer]: arrayMove(prev[toContainer], activeIndex, overIndex),
        }));
      }
    }
  }

  const updateStatus = async (taskId, fromStatus, toStatus) => {
    await post('/crm/tasks/status/update/' + taskId, { from_status: fromStatus, to_status: toStatus });
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('crm.tasks')} onReload={fetchTasks}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/crm/tasks/create')}>
            + {t('crm.newTask')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content scrollable">
          {loading ? (
            <div className="text-center py-5 text-muted">{t('common.loadingDots')}</div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={collisionDetection}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <div className="kanban-board">
                {columns.map(col => (
                  <KanbanColumn
                    key={col.id}
                    column={col}
                    taskIds={columnOrder[col.id]}
                    tasks={tasks}
                  />
                ))}
              </div>
              <DragOverlay>
                {activeId && tasks[activeId] && (
                  <div className="drag-overlay-card">
                    <TaskCardContent task={tasks[activeId]} />
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />
    </SidebarLayout>
  );
}

export default TasksPage;
